import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  RefreshCw,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Play,
  Clock,
  Calendar,
  Activity,
  ListTodo,
  Send,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

interface CronJobStatusRow {
  jobid: number | null
  jobname: string
  schedule: string | null
  command: string | null
  active: boolean | null
  exists_flag: boolean
}

interface CronRunContact {
  contact_id: string
  name: string | null
  wa_id: string | null
  message_status: string | null
}

interface CronRunRow {
  runid: number
  jobid: number
  status: string
  return_message: string | null
  start_time: string
  end_time: string | null
  duration_ms: number | null
  dispatched_count?: number
  contacts?: CronRunContact[]
}

interface PendingRow {
  active_count: number
  due_now_count: number
  blocked_count: number
}

interface FollowUpStateRow {
  id: string
  account_contact_id: string
  follow_up_id: string
  status: 'active' | 'completed' | 'paused' | 'blocked'
  next_interval_order: number
  next_fire_at: string | null
  next_fire_reason: string | null
  last_error: string | null
  updated_at: string
}

interface ScheduledFollowUpRow {
  id: string
  account_contact_id: string
  status: 'pending' | 'processing' | 'sent' | 'failed' | 'cancelled'
  scheduled_at: string
  sent_at: string | null
  error_message: string | null
  follow_up_id: string | null
  follow_up_interval_id: string | null
  created_at: string
}

interface FollowUpConfigRow {
  id: string
  name: string
  assistant_id: string
  is_active: boolean
  window_start: string
  window_end: string
  days_of_week: string[]
}

const REFRESH_INTERVAL_MS = 30_000

export default function DebugFollowUp() {
  const { user } = useAuth()
  const [status, setStatus] = useState<CronJobStatusRow | null>(null)
  const [runs, setRuns] = useState<CronRunRow[]>([])
  const [pending, setPending] = useState<PendingRow | null>(null)
  const [states, setStates] = useState<FollowUpStateRow[]>([])
  const [scheduled, setScheduled] = useState<ScheduledFollowUpRow[]>([])
  const [configs, setConfigs] = useState<FollowUpConfigRow[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [running, setRunning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastRefreshAt, setLastRefreshAt] = useState<Date | null>(null)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [manualRunResult, setManualRunResult] = useState<{ rows: number; at: Date } | null>(null)
  const isMountedRef = useRef(true)

  const fetchAll = useCallback(async () => {
    if (!supabase || !user?.account_id) return
    setRefreshing(true)
    setError(null)

    try {
      const [statusRes, runsRes, pendingRes, statesRes, scheduledRes, configRes] = await Promise.all([
        supabase.rpc('check_followup_cron_status'),
        supabase.rpc('check_followup_cron_recent_runs_detailed', { p_account_id: user.account_id, p_limit: 20 }),
        supabase.rpc('count_pending_followup_states'),
        supabase
          .from('account_contact_follow_up_states')
          .select('id, account_contact_id, follow_up_id, status, next_interval_order, next_fire_at, next_fire_reason, last_error, updated_at')
          .eq('account_id', user.account_id)
          .order('updated_at', { ascending: false })
          .limit(100),
        supabase
          .from('account_scheduled_messages')
          .select('id, account_contact_id, status, scheduled_at, sent_at, error_message, follow_up_id, follow_up_interval_id, created_at')
          .eq('account_id', user.account_id)
          .eq('source', 'followup')
          .order('created_at', { ascending: false })
          .limit(100),
        supabase
          .from('account_assistant_follow_ups')
          .select('id, name, assistant_id, is_active, window_start, window_end, days_of_week')
          .eq('account_id', user.account_id)
          .order('created_at', { ascending: false })
          .limit(100),
      ])

      if (statusRes.error) throw statusRes.error
      if (runsRes.error) throw runsRes.error
      if (pendingRes.error) throw pendingRes.error
      if (statesRes.error) throw statesRes.error
      if (scheduledRes.error) throw scheduledRes.error
      if (configRes.error) throw configRes.error

      if (!isMountedRef.current) return

      setStatus((statusRes.data as CronJobStatusRow[] | null)?.[0] ?? null)
      setRuns((runsRes.data as CronRunRow[] | null) ?? [])
      setPending((pendingRes.data as PendingRow[] | null)?.[0] ?? null)
      setStates((statesRes.data as FollowUpStateRow[] | null) ?? [])
      setScheduled((scheduledRes.data as ScheduledFollowUpRow[] | null) ?? [])
      setConfigs((configRes.data as FollowUpConfigRow[] | null) ?? [])
      setLastRefreshAt(new Date())
    } catch (err: any) {
      console.error('[DebugFollowUp] Erro ao carregar dados:', err)
      if (isMountedRef.current) {
        setError(err?.message || 'Erro ao carregar debug de follow-up')
      }
    } finally {
      if (isMountedRef.current) {
        setRefreshing(false)
        setLoading(false)
      }
    }
  }, [user?.account_id])

  useEffect(() => {
    isMountedRef.current = true
    fetchAll()
    return () => {
      isMountedRef.current = false
    }
  }, [fetchAll])

  useEffect(() => {
    if (!autoRefresh) return
    const id = window.setInterval(() => fetchAll(), REFRESH_INTERVAL_MS)
    return () => window.clearInterval(id)
  }, [autoRefresh, fetchAll])

  const handleRunNow = useCallback(async () => {
    if (!supabase || running) return
    setRunning(true)
    setError(null)
    try {
      const { data, error: runErr } = await supabase.rpc('run_dispatch_followup_candidates')
      if (runErr) throw runErr
      setManualRunResult({ rows: typeof data === 'number' ? data : 0, at: new Date() })
      await fetchAll()
    } catch (err: any) {
      console.error('[DebugFollowUp] Erro ao executar dispatcher:', err)
      setError(err?.message || 'Erro ao executar dispatcher de follow-up')
    } finally {
      if (isMountedRef.current) setRunning(false)
    }
  }, [running, fetchAll])

  const checks = useMemo(() => {
    const latestRun = runs[0]
    const latestRunSucceeded = latestRun?.status?.toLowerCase() === 'succeeded'
    const activeStates = pending?.active_count ?? 0
    const blockedStates = pending?.blocked_count ?? 0
    const dueNowStates = pending?.due_now_count ?? 0
    const followUpPending = scheduled.filter((m) => m.status === 'pending').length

    return [
      {
        id: 'job_exists',
        ok: !!status?.exists_flag,
        title: 'Cron de follow-up registrado',
        detail: status?.exists_flag ? `Job ${status.jobname} encontrado.` : 'Job não encontrado.',
      },
      {
        id: 'job_active',
        ok: !!status?.active,
        title: 'Cron ativo',
        detail: status?.active ? 'cron.job.active = true.' : 'cron.job.active = false.',
      },
      {
        id: 'latest_run',
        ok: !!latestRun && latestRunSucceeded,
        title: 'Última execução bem-sucedida',
        detail: latestRun
          ? `${latestRun.status} em ${new Date(latestRun.start_time).toLocaleTimeString('pt-BR')}`
          : 'Sem execuções ainda.',
      },
      {
        id: 'config_exists',
        ok: configs.some((c) => c.is_active),
        title: 'Follow-ups ativos configurados',
        detail: `${configs.filter((c) => c.is_active).length} regra(s) ativa(s) na conta.`,
      },
      {
        id: 'states',
        ok: activeStates > 0 || blockedStates > 0,
        title: 'Estados gerados para contatos',
        detail: `${activeStates} ativo(s), ${blockedStates} bloqueado(s), ${dueNowStates} vencido(s).`,
      },
      {
        id: 'queue',
        ok: followUpPending >= 0,
        title: 'Fila follow-up acessível',
        detail: `${scheduled.length} agendamento(s) follow-up, ${followUpPending} pendente(s).`,
      },
    ]
  }, [status, runs, pending, configs, scheduled])

  const overallOk = checks.every((c) => c.ok)

  return (
    <div className="w-full p-6 flex flex-col gap-6">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Debug Follow-up</h1>
          <p className="text-sm text-neutral-400 mt-2 max-w-3xl">
            Painel de saúde do fluxo de follow-up automático da conta: cron, estado dos contatos e mensagens agendadas com source followup.
          </p>
          {lastRefreshAt && (
            <p className="text-xs text-neutral-500 mt-1">
              Última atualização: {lastRefreshAt.toLocaleTimeString('pt-BR')}
              {autoRefresh && <span className="ml-2 text-neutral-600">(auto-refresh a cada 30s)</span>}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => setAutoRefresh((v) => !v)}
            className={autoRefresh ? 'bg-neutral-700 hover:bg-neutral-600' : 'bg-neutral-800 hover:bg-neutral-700'}
          >
            <Activity className="w-4 h-4 mr-2" />
            {autoRefresh ? 'Auto: ON' : 'Auto: OFF'}
          </Button>
          <Button onClick={fetchAll} disabled={refreshing} className="bg-neutral-700 hover:bg-neutral-600">
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <Button onClick={handleRunNow} disabled={running} className="bg-blue-600 hover:bg-blue-700">
            <Play className={`w-4 h-4 mr-2 ${running ? 'animate-pulse' : ''}`} />
            {running ? 'Executando...' : 'Executar dispatcher'}
          </Button>
        </div>
      </header>

      {error && (
        <Card className="bg-red-900/20 border-red-700 p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-400">{error}</span>
          </div>
        </Card>
      )}

      {manualRunResult && (
        <Card className="bg-blue-900/20 border-blue-700 p-4">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-blue-400" />
            <span className="text-blue-300">
              Dispatcher manual em {manualRunResult.at.toLocaleTimeString('pt-BR')}: <strong>{manualRunResult.rows}</strong> item(ns) acionado(s).
            </span>
          </div>
        </Card>
      )}

      <Card className={`p-6 border ${overallOk ? 'bg-green-900/10 border-green-700' : 'bg-yellow-900/10 border-yellow-700'}`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            {overallOk ? <CheckCircle2 className="w-5 h-5 text-green-400" /> : <AlertCircle className="w-5 h-5 text-yellow-400" />}
            {overallOk ? 'Tudo funcional' : 'Atenção'}
          </h2>
          <Badge className={overallOk ? 'bg-green-600' : 'bg-yellow-600'}>
            {checks.filter((c) => c.ok).length} / {checks.length} checks OK
          </Badge>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {checks.map((check) => (
            <div
              key={check.id}
              className={`flex items-start gap-3 p-3 rounded-lg border ${
                check.ok ? 'bg-green-900/10 border-green-700/40' : 'bg-yellow-900/10 border-yellow-700/40'
              }`}
            >
              {check.ok ? (
                <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
              ) : (
                <XCircle className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white">{check.title}</p>
                <p className="text-xs text-neutral-400 mt-0.5">{check.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="bg-neutral-800 border-neutral-700 p-6">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-400" />
            Cron
          </h2>
          {loading && !status ? (
            <SkeletonRows rows={4} />
          ) : (
            <div className="space-y-2 text-sm">
              <KeyValue label="Job" value={<span className="font-mono text-white">{status?.jobname || '—'}</span>} />
              <KeyValue label="Schedule" value={<span className="font-mono text-white">{status?.schedule || '—'}</span>} />
              <KeyValue
                label="Ativo"
                value={<Badge className={status?.active ? 'bg-green-600' : 'bg-red-600'}>{String(!!status?.active)}</Badge>}
              />
            </div>
          )}
        </Card>

        <Card className="bg-neutral-800 border-neutral-700 p-6">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <ListTodo className="w-5 h-5 text-yellow-400" />
            Estados Follow-up
          </h2>
          {loading && !pending ? (
            <SkeletonRows rows={4} />
          ) : (
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Ativos" value={pending?.active_count ?? 0} accent="green" />
              <Stat label="Vencidos" value={pending?.due_now_count ?? 0} accent={(pending?.due_now_count ?? 0) > 0 ? 'amber' : 'green'} />
              <Stat label="Bloqueados" value={pending?.blocked_count ?? 0} accent={(pending?.blocked_count ?? 0) > 0 ? 'amber' : 'green'} />
            </div>
          )}
        </Card>

        <Card className="bg-neutral-800 border-neutral-700 p-6">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <Send className="w-5 h-5 text-purple-400" />
            Fila da Conta
          </h2>
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Configs ativas" value={configs.filter((c) => c.is_active).length} accent="blue" />
            <Stat label="Agendadas" value={scheduled.length} accent="green" />
            <Stat label="Pendentes" value={scheduled.filter((m) => m.status === 'pending').length} accent="amber" />
            <Stat label="Falhas" value={scheduled.filter((m) => m.status === 'failed').length} accent={scheduled.some((m) => m.status === 'failed') ? 'red' : 'green'} />
          </div>
        </Card>
      </div>

      <Card className="bg-neutral-800 border-neutral-700 p-6">
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-purple-400" />
          Últimas execuções do cron
        </h2>
        {loading && runs.length === 0 ? (
          <SkeletonRows rows={5} />
        ) : runs.length === 0 ? (
          <div className="bg-neutral-900 p-6 rounded-lg text-center text-neutral-400 text-sm">
            Nenhuma execução registrada.
          </div>
        ) : (
          <SimpleRunTable runs={runs} />
        )}
      </Card>

      <Card className="bg-neutral-800 border-neutral-700 p-6">
        <h2 className="text-xl font-semibold text-white mb-4">Estados por contato (conta atual)</h2>
        {loading && states.length === 0 ? (
          <SkeletonRows rows={8} />
        ) : states.length === 0 ? (
          <EmptyState label="Nenhum estado de follow-up encontrado para esta conta." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-neutral-400 border-b border-neutral-700">
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium">Contato</th>
                  <th className="py-2 pr-4 font-medium">Follow-up</th>
                  <th className="py-2 pr-4 font-medium">Próximo disparo</th>
                  <th className="py-2 pr-4 font-medium">Motivo</th>
                  <th className="py-2 font-medium">Erro</th>
                </tr>
              </thead>
              <tbody>
                {states.map((row) => (
                  <tr key={row.id} className="border-b border-neutral-800 last:border-b-0 align-top">
                    <td className="py-2 pr-4"><Badge className={stateBadgeClass(row.status)}>{row.status}</Badge></td>
                    <td className="py-2 pr-4 text-neutral-200 font-mono text-xs">{row.account_contact_id}</td>
                    <td className="py-2 pr-4 text-neutral-300 font-mono text-xs">{row.follow_up_id}</td>
                    <td className="py-2 pr-4 text-neutral-300 whitespace-nowrap">
                      {row.next_fire_at ? new Date(row.next_fire_at).toLocaleString('pt-BR') : '—'}
                    </td>
                    <td className="py-2 pr-4 text-neutral-400 text-xs">{row.next_fire_reason || '—'}</td>
                    <td className="py-2 text-neutral-400 text-xs break-all">{row.last_error || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="bg-neutral-800 border-neutral-700 p-6">
        <h2 className="text-xl font-semibold text-white mb-4">Agendamentos follow-up (conta atual)</h2>
        {loading && scheduled.length === 0 ? (
          <SkeletonRows rows={8} />
        ) : scheduled.length === 0 ? (
          <EmptyState label="Nenhum agendamento com source='followup' encontrado na conta." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-neutral-400 border-b border-neutral-700">
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium">Contato</th>
                  <th className="py-2 pr-4 font-medium">Agendado para</th>
                  <th className="py-2 pr-4 font-medium">Enviado em</th>
                  <th className="py-2 pr-4 font-medium">Follow-up</th>
                  <th className="py-2 font-medium">Erro</th>
                </tr>
              </thead>
              <tbody>
                {scheduled.map((row) => (
                  <tr key={row.id} className="border-b border-neutral-800 last:border-b-0 align-top">
                    <td className="py-2 pr-4"><Badge className={scheduledBadgeClass(row.status)}>{row.status}</Badge></td>
                    <td className="py-2 pr-4 text-neutral-200 font-mono text-xs">{row.account_contact_id}</td>
                    <td className="py-2 pr-4 text-neutral-300 whitespace-nowrap">{new Date(row.scheduled_at).toLocaleString('pt-BR')}</td>
                    <td className="py-2 pr-4 text-neutral-300 whitespace-nowrap">{row.sent_at ? new Date(row.sent_at).toLocaleString('pt-BR') : '—'}</td>
                    <td className="py-2 pr-4 text-neutral-400 font-mono text-xs">{row.follow_up_id || '—'}</td>
                    <td className="py-2 text-neutral-400 text-xs break-all">{row.error_message || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

function KeyValue({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 py-1">
      <span className="text-neutral-400">{label}:</span>
      <span className="text-right">{value}</span>
    </div>
  )
}

function SkeletonRows({ rows }: { rows: number }) {
  return (
    <div className="space-y-2 animate-pulse">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-4 bg-neutral-700/50 rounded" />
      ))}
    </div>
  )
}

function EmptyState({ label }: { label: string }) {
  return <div className="bg-neutral-900 p-6 rounded-lg text-center text-neutral-400 text-sm">{label}</div>
}

function Stat({ label, value, accent }: { label: string; value: number; accent: 'green' | 'amber' | 'red' | 'blue' }) {
  const accentClass = {
    green: 'text-green-300 border-green-600/30 bg-green-500/5',
    amber: 'text-amber-300 border-amber-600/30 bg-amber-500/5',
    red: 'text-red-300 border-red-600/30 bg-red-500/5',
    blue: 'text-blue-300 border-blue-600/30 bg-blue-500/5',
  }[accent]
  return (
    <div className={`rounded-lg border p-3 ${accentClass}`}>
      <div className="text-[11px] uppercase tracking-wide opacity-80">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  )
}

function SimpleRunTable({ runs }: { runs: CronRunRow[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-neutral-400 border-b border-neutral-700">
            <th className="py-2 pr-4 font-medium">Status</th>
            <th className="py-2 pr-4 font-medium">Início</th>
            <th className="py-2 pr-4 font-medium">Duração</th>
            <th className="py-2 pr-4 font-medium">Run ID</th>
            <th className="py-2 pr-4 font-medium">Disparos</th>
            <th className="py-2 pr-4 font-medium">Contatos</th>
            <th className="py-2 font-medium">Mensagem</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((r) => {
            const contacts = r.contacts ?? []
            const dispatched = r.dispatched_count ?? contacts.length
            return (
              <tr key={r.runid} className="border-b border-neutral-800 last:border-b-0 align-top">
                <td className="py-2 pr-4"><Badge className={statusBadgeClass(r.status)}>{r.status}</Badge></td>
                <td className="py-2 pr-4 text-neutral-200 whitespace-nowrap">{new Date(r.start_time).toLocaleString('pt-BR')}</td>
                <td className="py-2 pr-4 text-neutral-300 whitespace-nowrap">{r.duration_ms === null ? '—' : `${r.duration_ms} ms`}</td>
                <td className="py-2 pr-4 text-neutral-400 font-mono text-xs">{r.runid}</td>
                <td className="py-2 pr-4">
                  {dispatched > 0 ? (
                    <Badge className="bg-purple-600">{dispatched}</Badge>
                  ) : (
                    <span className="text-neutral-500 text-xs">0</span>
                  )}
                </td>
                <td className="py-2 pr-4 min-w-[220px]">
                  {contacts.length === 0 ? (
                    <span className="text-neutral-500 text-xs">—</span>
                  ) : (
                    <div className="flex flex-col gap-1">
                      {contacts.map((c, idx) => (
                        <div key={`${r.runid}-${c.contact_id}-${idx}`} className="flex items-center gap-2">
                          <span className="text-neutral-200 text-xs font-medium truncate max-w-[160px]">
                            {c.name || c.wa_id || 'Sem nome'}
                          </span>
                          {c.wa_id && (
                            <span className="text-neutral-500 font-mono text-[11px]">{c.wa_id}</span>
                          )}
                          {c.message_status && (
                            <Badge className={`${scheduledBadgeClass(c.message_status as ScheduledFollowUpRow['status'])} text-[10px] px-1.5 py-0`}>
                              {c.message_status}
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </td>
                <td className="py-2 text-neutral-300 font-mono text-xs break-all">{r.return_message || '—'}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function statusBadgeClass(status: string): string {
  const s = status.toLowerCase()
  if (s === 'succeeded') return 'bg-green-600'
  if (s === 'failed') return 'bg-red-600'
  if (s === 'running' || s === 'starting') return 'bg-blue-600'
  return 'bg-neutral-600'
}

function stateBadgeClass(status: FollowUpStateRow['status']): string {
  if (status === 'active') return 'bg-green-600'
  if (status === 'blocked') return 'bg-yellow-600'
  if (status === 'paused') return 'bg-blue-600'
  return 'bg-neutral-600'
}

function scheduledBadgeClass(status: ScheduledFollowUpRow['status']): string {
  if (status === 'sent') return 'bg-green-600'
  if (status === 'failed') return 'bg-red-600'
  if (status === 'processing') return 'bg-blue-600'
  if (status === 'pending') return 'bg-yellow-600'
  return 'bg-neutral-600'
}
